/* eslint-disable @typescript-eslint/camelcase */

import React from 'react'
import { IntlProvider } from 'react-intl'
import { ThemeProvider } from 'styled-components'
import { theme } from '@elamajs/ui-kit'

import { Chart, Tooltip } from '../Visualisation/components'

import * as Styled from '../Visualisation/Visualisation.styled'

import {
  CPMChart,
  CoverageChart,
  CPVChart,
  CostChart,
  ERChart,
  BudgetAndQuantityChart
} from './charts'
import { useData } from './hooks'
import { ChartSize } from '../Visualisation/types'
import { getAverageData } from '../Visualisation/helpers'
import Filters from './Filters'

import * as AnalyticsStyled from './AnalyticsNative.styled'

const Visualisation = () => {
  const { data, fetchData, isFetching } = useData()

  return (
    <>
      <AnalyticsStyled.Title>Аналитика Telegram по&nbsp;тематикам</AnalyticsStyled.Title>
      <Styled.Main data-sticky-container>
        <Filters data={data} isFetching={isFetching} fetchData={fetchData} />
        <Styled.Body>
          <Styled.Row>
            <Styled.Title>
              Сколько стоят 1000 показов и&nbsp;один клик по&nbsp;ссылке в&nbsp;интеграции
            </Styled.Title>
            <Styled.FlexContainer>
              <Chart
                graphHeight={ChartSize.LARGE}
                title={
                  <>
                    Стоимость 1000 показов (CPM) в&nbsp;Telegram&nbsp;
                    <Styled.Tooltip as={Tooltip} width="293px" direction="top-start">
                      <span>
                        CPM = сумма затрат на&nbsp;все размещения &divide; суммарное количество
                        показов (подписчики &times; ER) &times; 1000
                      </span>
                    </Styled.Tooltip>
                  </>
                }
                data={data?.data.cpm}
                withAverage
                averageCurrencyType="RUB"
                averageHiddenOnMobile={false}
                calculateAverage={data => Math.round(getAverageData(data) ?? 0)}
              >
                <CPMChart toFixed={0} data={data?.data.cpm} graphHeight={ChartSize.LARGE} />
              </Chart>
              <Chart
                graphHeight={ChartSize.LARGE}
                title={
                  <>
                    Стоимость клика (CPC) в&nbsp;Telegram&nbsp;
                    <Styled.Tooltip as={Tooltip} width="293px" direction="top-start">
                      <span>
                        Средний CPC = сумма затрат на&nbsp;все рекламные публикации &divide; общее
                        количество кликов
                      </span>
                    </Styled.Tooltip>
                  </>
                }
                data={data?.data.cpc}
                withAverage
                averageCurrencyType="RUB"
                averageHiddenOnMobile={false}
              >
                <CPMChart toFixed={1} data={data?.data.cpc} graphHeight={ChartSize.LARGE} />
              </Chart>
            </Styled.FlexContainer>
          </Styled.Row>
          <Styled.Row>
            <Styled.Title>
              Какой охват получают интеграции и&nbsp;как вовлекают аудиторию
            </Styled.Title>
            <Styled.FlexContainer>
              <Chart
                graphHeight={ChartSize.LARGE}
                title={
                  <>
                    Охват на&nbsp;одну публикацию&nbsp;
                    <Styled.Tooltip as={Tooltip} width="293px" direction="top-start">
                      <span>
                        Средний показатель охвата на&nbsp;одну публикацию в&nbsp;каналах тематики
                      </span>
                    </Styled.Tooltip>
                  </>
                }
                data={data?.data.coverage}
                withAverage
                averageHiddenOnMobile={false}
                calculateAverage={data => Math.round(getAverageData(data) ?? 0)}
              >
                <CoverageChart data={data?.data.coverage} graphHeight={ChartSize.LARGE} />
              </Chart>
              <Chart
                graphHeight={ChartSize.LARGE}
                title={
                  <>
                    ER публикации, %&nbsp;
                    <Styled.Tooltip as={Tooltip} width="293px" direction="top-start">
                      <span>
                        Средний ER = сумма кликов по&nbsp;всем рекламным публикациям в&nbsp;тематике
                        &divide; сумма подписчиков всех каналов, где они размещались
                        (в&nbsp;процентах)
                      </span>
                    </Styled.Tooltip>
                  </>
                }
                data={data?.data.er}
                withAverage
                averageHiddenOnMobile={false}
              >
                <ERChart data={data?.data.er} graphHeight={ChartSize.LARGE} />
              </Chart>
            </Styled.FlexContainer>
          </Styled.Row>
          <Styled.Row>
            <Styled.Title>Стоимость просмотра на&nbsp;одну публикацию</Styled.Title>
            <Styled.FlexContainer>
              <Chart
                graphHeight={ChartSize.LARGE}
                data={data?.data.cpv}
                title={
                  <>
                    CPV на&nbsp;одну публикацию&nbsp;
                    <Styled.Tooltip as={Tooltip} width="293px" direction="top-start">
                      <span>
                        Средний CPV = сумма затрат на&nbsp;все рекламные посты &divide; общее
                        количество просмотров
                      </span>
                    </Styled.Tooltip>
                  </>
                }
                withAverage
                averageCurrencyType="RUB"
                averageHiddenOnMobile={false}
              >
                <CPVChart data={data?.data.cpv} graphHeight={ChartSize.LARGE} />
              </Chart>
              <Chart
                graphHeight={ChartSize.LARGE}
                data={data?.data.cost}
                title={
                  <>
                    Стоимость одной публикации&nbsp;
                    <Styled.Tooltip as={Tooltip} width="293px" direction="top-start">
                      <span>
                        Средний показатель стоимости одной публикации в&nbsp;каналах тематики
                      </span>
                    </Styled.Tooltip>
                  </>
                }
                withAverage
                averageCurrencyType="RUB"
                calculateAverage={data => Math.round(getAverageData(data) ?? 0)}
                averageHiddenOnMobile={false}
              >
                <CostChart data={data?.data.cost} graphHeight={ChartSize.LARGE} />
              </Chart>
            </Styled.FlexContainer>
          </Styled.Row>
          <Styled.Row>
            <Styled.Title>
              Бюджет и&nbsp;количество публикаций на&nbsp;одного рекламодателя
            </Styled.Title>
            <Chart
              graphHeight={ChartSize.LARGE}
              legend={[
                {
                  color: '#C88CF2',
                  title: <>Средний бюджет одного рекламодателя</>
                },
                {
                  color: '#3A64F8',
                  title: <>Среднее количество публикаций одного рекламодателя</>
                }
              ]}
              hasData={!!(data?.data?.budget && data?.data?.quantity)}
            >
              <BudgetAndQuantityChart
                graphHeight={ChartSize.LARGE}
                data={
                  data?.data?.budget && data?.data?.quantity
                    ? { budget: data.data.budget, quantity: data.data.quantity }
                    : undefined
                }
              />
            </Chart>
          </Styled.Row>
        </Styled.Body>
      </Styled.Main>
    </>
  )
}

const withProviders = (Component: React.ComponentType) => {
  const NewComponent: React.ComponentType = () => {
    return (
      <ThemeProvider theme={theme}>
        <IntlProvider locale="ru">
          <Component />
        </IntlProvider>
      </ThemeProvider>
    )
  }

  return NewComponent
}

export default withProviders(Visualisation)
